import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  AccountInfo,
  InteractionStatus,
  EventType,
  EventMessage,
} from '@azure/msal-browser';
import { GraphUserService } from './graph-user.service';
import { TokenService } from './token.service';
import { RoleService } from './role.service';
import { environment } from './../../../../environments/environment';
import { catchError, filter, first, switchMap } from 'rxjs/operators';
import { throwError, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _msalService = inject(MsalService);
  private readonly _msalBroadcastService = inject(MsalBroadcastService);
  private readonly _graphUserService = inject(GraphUserService);
  private readonly _tokenService = inject(TokenService);
  private readonly _roleService = inject(RoleService);

  public authReady: WritableSignal<boolean> = signal(false);
  private readonly _initialAuthResolutionAttempted = signal(false);

  constructor() {
    this._msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        first()
      )
      .subscribe(() => {
        this.attemptInitialAuthResolution();
      });

    this._msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        )
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        if (payload && payload.account) {
          this.setActiveAccount(payload.account);
          if (
            !this.authReady() &&
            payload.scopes.some(
              (s) =>
                s.toLowerCase() ===
                environment.msalConfig.apiScope.toLowerCase()
            )
          ) {
            this.setToken(payload.accessToken);
          } else if (
            !this.authReady() &&
            result.eventType === EventType.LOGIN_SUCCESS
          ) {
            this.acquireTokenForApp(payload.account).subscribe({
              next: (apiTokenResponse) =>
                this.setToken(apiTokenResponse.accessToken),
              error: (err) => {
                console.log(
                  '[AuthService] Error acquiring API token after generic LOGIN_SUCCESS in msalSubject$:',
                  err
                );
                this.clearToken();
              },
            });
          }
        }
      });

    this._msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg) =>
            msg.eventType === EventType.LOGOUT_SUCCESS ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe(() => {
        this.clearToken();
      });
  }

  private attemptInitialAuthResolution(): void {
    if (this._initialAuthResolutionAttempted()) {
      return;
    }
    this._initialAuthResolutionAttempted.set(true);

    this.setActiveAccount();
    const msalAccount = this._msalService.instance.getActiveAccount();

    if (msalAccount) {
      this.acquireTokenForApp(msalAccount).subscribe({
        next: (apiTokenResponse) => {
          this.setToken(apiTokenResponse.accessToken);
        },
        error: (err) => {
          console.log(
            '[AuthService] attemptInitialAuthResolution: Failed to acquire API token.',
            err
          );
          this.clearToken();
        },
      });
    } else {
      this.authReady.set(false);
    }
  }

  public setActiveAccount(account?: AccountInfo | null): void {
    let activeAccount = account;
    if (!activeAccount) {
      const allAccounts = this._msalService.instance.getAllAccounts();
      if (allAccounts.length > 0) {
        activeAccount = allAccounts[0];
      }
    }
    if (activeAccount) {
      if (
        this._msalService.instance.getActiveAccount()?.homeAccountId !==
        activeAccount.homeAccountId
      ) {
        this._msalService.instance.setActiveAccount(activeAccount);
      }
    }
  }

  private acquireTokenForApp(
    account?: AccountInfo | null
  ): Observable<AuthenticationResult> {
    const currentAccount =
      account || this._msalService.instance.getActiveAccount();
    if (!currentAccount) {
      console.log(
        '[AuthService] acquireTokenForApp: No active account to acquire token for.'
      );
      return throwError(
        () => new Error('No active account for acquireTokenSilent.')
      );
    }
    return this._msalService
      .acquireTokenSilent({
        scopes: [environment.msalConfig.apiScope],
        account: currentAccount,
      })
      .pipe(
        catchError((error) => {
          console.warn(
            '[AuthService] acquireTokenForApp: Silent token acquisition failed.',
            error
          );
          if (error instanceof InteractionRequiredAuthError) {
            console.log(
              '[AuthService] acquireTokenForApp: Interaction required. Initiating acquireTokenRedirect.'
            );
            this._msalService.acquireTokenRedirect({
              scopes: [environment.msalConfig.apiScope],
            });

            return new Observable<never>(() => {});
          }
          return throwError(() => error);
        })
      );
  }

  public handleRedirect() {
    this._msalService
      .handleRedirectObservable()
      .pipe(
        filter(
          (
            msalResponse: AuthenticationResult | null
          ): msalResponse is AuthenticationResult =>
            msalResponse?.account != null
        ),
        first()
      )
      .subscribe({
        next: (msalResponse) => {
          this.authReady.set(false);
          this.setActiveAccount(msalResponse.account);
          this.acquireTokenForApp(msalResponse.account).subscribe({
            next: (apiTokenResponse) => {
              this.setToken(apiTokenResponse.accessToken);
            },
            error: (err) => {
              console.log(
                '[AuthService] handleRedirect: Error acquiring API token after redirect.',
                err
              );
              this.clearToken();
            },
          });
        },
        error: (err) => {
          this.clearToken();
        },
        complete: () => {
          console.log(
            '[AuthService] handleRedirectObservable completed. If not a redirect login, initial check will handle auth state.'
          );
        },
      });
  }

  public getToken(): string | null {
    return this._tokenService.getToken();
  }

  setToken(token: string) {
    this._tokenService.setToken(token);
    this._roleService.initRoles();

    if (this._roleService.userRoles().length > 0) {
      this.authReady.set(true);
    } else {
      this.authReady.set(true);
    }
  }

  clearToken() {
    this._tokenService.clearToken();
    this._roleService.clearRoles();
    this.authReady.set(false);
  }

  public isLoggedIn(): boolean {
    const msalAccountActive =
      this._msalService.instance.getActiveAccount() !== null;
    return msalAccountActive && this.authReady();
  }

  public loginRedirect() {
    this.authReady.set(false);
    this._msalService.loginRedirect();
  }

  public logout() {
    const activeAccount = this._msalService.instance.getActiveAccount();
    this.clearToken();
    this._msalService.logoutRedirect({ account: activeAccount });
  }

  public checkAndRenewToken(scopes: string[]): void {
    const account = this._msalService.instance.getActiveAccount();
    if (!account) {
      console.warn(
        '[AuthService] checkAndRenewToken: No active MSAL account. Cannot renew.'
      );
      if (this.authReady()) {
        this.clearToken();
      }
      return;
    }

    this.acquireTokenForApp(account).subscribe({
      next: (response) => {
        this.setToken(response.accessToken);
      },
      error: (error) => {
        console.log(
          '[AuthService] checkAndRenewToken: Failed to ensure/renew token for app API.',
          error
        );
        if (this.authReady()) {
          this.clearToken();
        }
      },
    });
  }

  public refreshToken(): Observable<string> {
    const account = this._msalService.instance.getActiveAccount();
    if (!account) {
      return throwError(
        () => new Error('No active account for token refresh.')
      );
    }

    return this._msalService
      .acquireTokenSilent({
        scopes: [environment.msalConfig.apiScope],
        account: account,
      })
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        }),
        switchMap((response) => {
          if (response?.accessToken) {
            this.setToken(response.accessToken);
            return of(response.accessToken);
          }
          return throwError(() => new Error('No access token in response.'));
        })
      );
  }

  public getUserProfile() {
    this._graphUserService.getUserProfile().subscribe({
      next: (user) => {
        this._graphUserService.graphUser.set(user);
      },
      error: (error) => {
        console.log('Error fetching user profile:', error);
      },
    });
  }
}
